import { useEffect, useMemo, useState, type ReactNode } from 'react';

export type NotificationTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'mention';

export interface Notification {
  id: string;
  /** Tone channel — drives the left stripe + icon tint. */
  tone?: NotificationTone;
  /** Title line. */
  title: ReactNode;
  /** Optional body under the title. */
  body?: ReactNode;
  /** Leading slot — usually an Avatar or icon. */
  leading?: ReactNode;
  /** Relative time label (e.g. "2 min ago"). Pre-formatted by caller. */
  time?: ReactNode;
  /** True → item is unread (thicker text + dot indicator). */
  unread?: boolean;
  /** Tab this notification belongs to (when using built-in tabs). */
  tab?: string;
  /** Action buttons at the bottom of the item. */
  actions?: ReactNode;
  /** Click handler for the whole row. */
  onClick?: () => void;
}

export interface NotificationCenterProps {
  /** Full list of notifications. The component filters by active tab. */
  notifications: ReadonlyArray<Notification>;
  /** Tab definitions. When empty, tabs bar is hidden. */
  tabs?: ReadonlyArray<{ id: string; label: ReactNode; filter?: (n: Notification) => boolean }>;
  /** Controlled active tab. */
  activeTab?: string;
  defaultTab?: string;
  onTabChange?: (tab: string) => void;
  /** Empty-state content when the active tab has no items. */
  emptyState?: ReactNode;
  /** Header slot — title + optional trailing buttons (mark all read etc.). */
  header?: ReactNode;
  /** Footer slot — typically a "View all" link. */
  footer?: ReactNode;
  /** Landmark label. */
  'aria-label'?: string;
  className?: string;
}

/** Floating notification panel. Typically mounted inside a Popover
 *  anchored to a bell icon. The component itself is layout-only —
 *  consumers own positioning.
 *
 *  Layout:
 *   ┌───────────────────────────────┐
 *   │ HEADER (title + actions)       │
 *   ├───────────────────────────────┤
 *   │ [All] [Unread] [Mentions]      │  ← tabs bar (optional)
 *   ├───────────────────────────────┤
 *   │ • item 1                       │
 *   │ • item 2                       │  ← filtered list
 *   │ • item 3                       │
 *   ├───────────────────────────────┤
 *   │ FOOTER (view all)              │
 *   └───────────────────────────────┘
 */
export function NotificationCenter(props: NotificationCenterProps): ReactNode {
  const {
    notifications,
    tabs,
    activeTab,
    defaultTab,
    onTabChange,
    emptyState,
    header,
    footer,
    className,
    'aria-label': ariaLabel,
  } = props;

  const [internalTab, setInternalTab] = useState<string | undefined>(defaultTab ?? tabs?.[0]?.id);
  const current = activeTab ?? internalTab;

  // Sync internalTab when `tabs` arrives lazily (e.g. fetched after the
  // initial render). Without this, `internalTab` stays `undefined`
  // forever and `current` matches no tab — every notification falls
  // through the filter and the user sees the whole list until they
  // manually click a tab.
  useEffect(() => {
    if (activeTab !== undefined) return;
    if (internalTab !== undefined) return;
    const first = defaultTab ?? tabs?.[0]?.id;
    if (first !== undefined) setInternalTab(first);
  }, [activeTab, internalTab, defaultTab, tabs]);

  const setTab = (id: string): void => {
    if (activeTab === undefined) setInternalTab(id);
    onTabChange?.(id);
  };

  const visible = useMemo(() => {
    if (tabs === undefined) return notifications;
    const active = tabs.find((t) => t.id === current);
    if (active === undefined || active.filter === undefined) return notifications;
    return notifications.filter(active.filter);
  }, [notifications, tabs, current]);

  return (
    <section
      className={['vq-notifs', className].filter(Boolean).join(' ')}
      aria-label={ariaLabel ?? 'Notifications'}
      data-component="NotificationCenter"
    >
      {header !== undefined && <header className="vq-notifs__head">{header}</header>}
      {tabs !== undefined && tabs.length > 0 && (
        <nav className="vq-notifs__tabs" role="tablist" aria-label="Notification filters">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={t.id === current}
              className="vq-notifs__tab"
              onClick={() => {
                setTab(t.id);
              }}
              data-active={t.id === current ? 'true' : 'false'}
            >
              {t.label}
            </button>
          ))}
        </nav>
      )}
      <ul className="vq-notifs__list">
        {visible.length === 0 ? (
          <li className="vq-notifs__empty">{emptyState ?? 'No notifications'}</li>
        ) : (
          visible.map((n) => {
            const onClick = n.onClick;
            const tone = n.tone ?? 'neutral';
            const body = (includeActions: boolean): ReactNode => (
              <>
                {n.leading !== undefined && <span className="vq-notifs__leading">{n.leading}</span>}
                <span className="vq-notifs__body">
                  <span className="vq-notifs__title-row">
                    <span className="vq-notifs__title">{n.title}</span>
                    {n.time !== undefined && <span className="vq-notifs__time">{n.time}</span>}
                  </span>
                  {n.body !== undefined && <span className="vq-notifs__text">{n.body}</span>}
                  {includeActions && n.actions !== undefined && (
                    <span className="vq-notifs__actions">{n.actions}</span>
                  )}
                </span>
                {n.unread === true && <span className="vq-notifs__unread" aria-label="Unread" />}
              </>
            );
            return (
              <li
                key={n.id}
                className="vq-notifs__item"
                data-tone={tone}
                data-unread={n.unread === true ? 'true' : 'false'}
              >
                {onClick !== undefined && n.actions !== undefined ? (
                  <div className="vq-notifs__item-btn vq-notifs__item-btn--split">
                    <button type="button" className="vq-notifs__item-main" onClick={onClick}>
                      {body(false)}
                    </button>
                    <span className="vq-notifs__actions vq-notifs__actions--standalone">
                      {n.actions}
                    </span>
                  </div>
                ) : onClick !== undefined ? (
                  <button type="button" className="vq-notifs__item-btn" onClick={onClick}>
                    {body(true)}
                  </button>
                ) : (
                  <div className="vq-notifs__item-btn">{body(true)}</div>
                )}
              </li>
            );
          })
        )}
      </ul>
      {footer !== undefined && <footer className="vq-notifs__foot">{footer}</footer>}
    </section>
  );
}
